import React from 'react';
import { Zap } from 'lucide-react';
import { Card, pnlCls, money } from './LearningParts';
import ProfileSuggestions from './ProfileSuggestions';

const KIND_TR = { PATLAMA: '💥 PATLAMA', ISINMA: '🔥 ISINIYOR', OYNAK: '🌊 OYNAK', AC: '🎯 AÇ', KUCUK_AC: '🚀 KÜÇÜK AÇ', ETIKET: '🔥 ETİKET', HABER: '📰 HABER' };

/** ⚡ Hızlı Kâr Botu'nun öğrenme/trader durumu (öğrenme sayfasında) + "bunu uygula" önerileri. */
export default function ScalperLearningCard({ sc, mode = 'paper', onChanged }) {
    if (!sc) return null;
    const t = sc.trader || {};
    const b = sc.balance || {};
    const rr = b.avg_loss ? Math.abs(b.avg_win / b.avg_loss) : null;
    return (
        <Card title="⚡ Hızlı Kâr Botu — öğrenme ve trader durumu" testId="learn-scalper" right={<span className="text-[10px] text-slate-500">son {sc.days} gün · {sc.sample} kapanış · toplam {sc.total}</span>}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="rounded-md border border-slate-800 bg-slate-950/50 px-2.5 py-2 flex items-center gap-3" data-testid="learn-scalper-level">
                    <div className="h-10 w-10 rounded-lg bg-amber-500 grid place-items-center text-slate-950 font-black"><Zap className="h-5 w-5" /></div>
                    <div>
                        <div className="text-sm font-bold text-slate-50">Seviye {sc.level?.level} · {sc.level?.name}</div>
                        <div className="text-[10px] text-slate-400">{sc.level?.total} kapanış{sc.level?.next_at ? ` · ${sc.level.next_name} için ${sc.level.remaining} daha` : ''}</div>
                    </div>
                </div>
                <div className="rounded-md border border-slate-800 bg-slate-950/50 px-2.5 py-2" data-testid="learn-scalper-trader">
                    <div className="text-sm font-bold text-slate-50">Trader notu: {t.name}</div>
                    <div className="text-[10px] text-slate-400">{t.desc}{t.grade != null ? ` · kâr faktörü ${t.profit_factor} · isabet %${t.win_rate}${t.breakeven_win_rate != null ? ` (başabaş için %${t.breakeven_win_rate})` : ''}` : ''}</div>
                </div>
            </div>
            {sc.sample > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                    <Stat label="Net" value={money(b.pnl_usd)} cls={pnlCls(b.pnl_usd)} hint={`brüt kâr +${Number(b.gross_profit).toFixed(1)}$ / brüt zarar ${Number(b.gross_loss).toFixed(1)}$`} testId="learn-scalper-net" />
                    <Stat label="Ort. kâr / ort. zarar" value={`${Number(b.avg_win).toFixed(1)}$ / ${Number(b.avg_loss).toFixed(1)}$`} cls={rr != null && rr >= 1 ? 'text-emerald-300' : 'text-amber-300'} hint={rr != null ? `oran ${rr.toFixed(2)}` : ''} testId="learn-scalper-rr" />
                    <Stat label="Kapanışlar" value={`${b.reasons?.tp ?? 0} hedef · ${b.reasons?.stop ?? 0} stop · ${b.reasons?.time ?? 0} zaman`} cls="text-slate-200" hint={sc.avg_minutes != null ? `ort. ${sc.avg_minutes} dk/işlem` : ''} testId="learn-scalper-reasons" />
                    <Stat label="Kaynak türleri" value={(sc.kinds || []).slice(0, 3).map((k) => `${KIND_TR[k.kind] || k.kind} ${k.n}`).join(' · ') || '—'} cls="text-slate-200" testId="learn-scalper-kinds" />
                </div>
            )}
            {sc.tips?.length > 0 && (
                <ul className="space-y-0.5" data-testid="learn-scalper-tips">
                    {sc.tips.map((x, i) => <li key={i} className="text-[11px] text-amber-100">• {x}</li>)}
                </ul>
            )}
            {sc.coins && (sc.coins.best.length + sc.coins.worst.length) > 0 && (
                <div className="text-[10px] text-slate-400" data-testid="learn-scalper-coins">
                    En iyi: {sc.coins.best.slice(0, 3).map((c) => `${c.symbol.replace('USDT', '')} ${money(c.pnl_usd)}`).join(' · ') || '—'} · En kötü: {sc.coins.worst.slice(0, 3).map((c) => `${c.symbol.replace('USDT', '')} ${money(c.pnl_usd)}`).join(' · ') || '—'}
                </div>
            )}
            {sc.sample === 0 && <div className="text-[11px] text-slate-400">Bu defterde son {sc.days} günde kapanan hızlı bot işlemi yok.</div>}
            <div className="border-t border-slate-800 pt-2">
                <ProfileSuggestions bot="scalper" mode={mode} suggestions={sc.suggestions || []} profile={sc.profile} onChanged={onChanged} />
            </div>
        </Card>
    );
}

function Stat({ label, value, hint, testId, cls = 'text-slate-100' }) {
    return (
        <div className="rounded-md border border-slate-800 bg-slate-950/50 px-2 py-1.5" data-testid={testId}>
            <div className="text-[10px] text-slate-500">{label}</div>
            <div className={`font-mono font-bold ${cls}`}>{value}</div>
            {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
        </div>
    );
}
